import { of } from 'rxjs';
import { LOADING_STATES } from '@/constants';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { connectAggregationToObservable } from '@pendo/agg-connect';
import { convertConfigToTimeSeries } from '../common/time-series-picker/time-series-picker.util.js';

export const PES_BUILD_OPTIONS = {
    'Product-Engagement-Score': {
        metricAlias: 'metrics',
        statusAlias: 'pesMetrics',
        errorMessage: 'Unable to load Product Engagement Score.',
    },
    'Product-Engagement-Score-Trend': {
        metricAlias: 'metricsFromPreviousPeriod',
        statusAlias: 'pesTrend',
        errorMessage: 'Unable to load trend data for Product Engagement Score.',
    },
    'Product-Engagement-Score-Trending-Second': {
        metricAlias: 'metricsFromSecondTrendingPeriod',
        statusAlias: 'pesTrendingSecond',
        errorMessage: 'Unable to load trending Product Engagement Score data.',
    },
    'Product-Engagement-Score-Trending-First': {
        metricAlias: 'metricsFromFirstTrendingPeriod',
        statusAlias: 'pesTrendingFirst',
        errorMessage: 'Unable to load trending Product Engagement Score data.',
    },
};

export function getAbbreviation (period, type) {
    const prefix = {
        daily: 'd',
        weekly: 'w',
        monthly: 'm',
    }[period];

    if (prefix === undefined) {
        throw new Error(`Unrecognized period identifier ${period}`);
    }

    const suffix = {
        visitors: 'u',
        accounts: 'a',
    }[type];

    if (suffix === undefined) {
        throw new Error(`Unrecognized stickiness type identifier ${type}`);
    }

    return `${prefix}a${suffix}`;
}

export function createPreviousTimeSeries ({ config, spec }) {
    const { start, end } = config;
    const { count } = spec;
    const getPreviousDate = (date, numDays) => {
        return moment(date, { ignoreTimeZone: true })
            .subtract(numDays, 'days')
            .startOf('day')
            .format(DATE_FORMAT.iso);
    };

    return convertConfigToTimeSeries({
        start: getPreviousDate(start, count),
        end: getPreviousDate(end, count),
        type: 'Custom',
    });
}

export function createNthPreviousTimeSeries ({ config, spec }, numOfPreviousSeries = 1) {
    if (numOfPreviousSeries > 1) {
        return createNthPreviousTimeSeries(createPreviousTimeSeries({ config, spec }), numOfPreviousSeries - 1);
    }

    return createPreviousTimeSeries({ config, spec });
}

export function hasEnoughData (firstEventTime, timeSeries, numOfMetricPeriods = 1) {
    const end = moment(timeSeries.config.end, { ignoreTimeZone: true }).valueOf();
    const start = moment(timeSeries.config.start, { ignoreTimeZone: true }).valueOf();

    return firstEventTime <= start - (end - start) * numOfMetricPeriods && moment().valueOf() > end;
}

export async function sendAggregation (agg, props) {
    return new Promise((resolve, reject) => {
        const observable = connectAggregationToObservable(agg, of({ ...props }));
        const subscription = observable.subscribe(({ status, value, error }) => {
            if (status === LOADING_STATES.RESOLVED || status === LOADING_STATES.REJECTED) {
                if (subscription) subscription.unsubscribe();

                if (status === LOADING_STATES.RESOLVED) {
                    return resolve(value);
                }

                if (status === LOADING_STATES.REJECTED) {
                    reject(error || new Error('Failed to fetch aggregation for unknown reason'));
                }
            }
        });
    });
}

export function mapPesAggResponseToState ({
    periodStart,
    periodEnd,
    adoptionMetric: adoption,
    growthMetric: growth,
    growthChurned,
    growthNew,
    growthResurrected,
    productEngagementScore,
    stickinessMetric: stickiness,
}) {
    return {
        periodStart,
        periodEnd,
        adoption,
        growth,
        growthChurned,
        growthNew,
        growthResurrected,
        productEngagementScore,
        stickiness,
    };
}
